import { QueryClient } from '@tanstack/react-query';

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      retry: 1,
    },
  },
});

// In local dev, call the Vite server and let it proxy /api to the backend.
// A separate :9000 URL is blocked by the browser when the page is opened
// from another host (for example a LAN IP).
const API_BASE_URL =
  (import.meta as any).env?.VITE_API_BASE_URL ||
  (import.meta.env.DEV
    ? ''
    : `${window.location.protocol}//${window.location.hostname}:9000`);

export async function apiRequest(
  method: string,
  endpoint: string,
  data?: any,
  options: { token?: string } = {}
) {
  const url = `${API_BASE_URL}${endpoint}`;
  const token = options.token || localStorage.getItem('access_token');
  
  const config: RequestInit = {
    method,
    headers: {
      'Content-Type': 'application/json',
      ...(token && { Authorization: `Bearer ${token}` }),
    },
    credentials: 'include',
  };

  if (data) {
    config.body = JSON.stringify(data);
  }

  try {
    const response = await fetch(url, config);

    if (!response.ok) {
      let errorMessage;
      try {
        const errorData = await response.json();
        errorMessage = errorData.detail || errorData.message || `HTTP ${response.status}: ${response.statusText}`;
      } catch {
        const errorText = await response.text();
        errorMessage = errorText || `HTTP ${response.status}: ${response.statusText}`;
      }
      throw new Error(errorMessage);
    }

    return response;
  } catch (error) {
    if (error instanceof Error) {
      throw error;
    }
    throw new Error('Network error or unexpected response');
  }
}

export function getQueryFn({ on401 = 'throw' }: { on401?: 'throw' | 'returnNull' } = {}) {
  return async ({ queryKey }: { queryKey: readonly unknown[] }) => {
    try {
      const endpoint = String(queryKey[0]);
      
      // Handle query parameters if provided as second element
      let fullUrl = endpoint;
      if (queryKey.length > 1 && typeof queryKey[1] === 'object' && queryKey[1] !== null) {
        const params = new URLSearchParams();
        Object.entries(queryKey[1] as Record<string, any>).forEach(([key, value]) => {
          if (value !== null && value !== undefined) {
            params.append(key, String(value));
          }
        });
        const queryString = params.toString();
        if (queryString) {
          fullUrl = `${endpoint}?${queryString}`;
        }
      }
      
      const response = await apiRequest('GET', fullUrl);
      if (response.status === 204) return null;
      return response.json();
    } catch (error: any) {
      if (error.message.includes('401') && on401 === 'returnNull') {
        return null;
      }
      throw error;
    }
  };
}
